// Frequently asked questions for first-time owners of a cocker spaniel puppy
// in a UK city flat. Answers are short and practical, and consistent with
// docs/RESEARCH.md and the guides. Anything medical points to the vet;
// emergencies say "call the vet now". Answers use the Prose markdown subset
// described in types.ts (paragraphs, "- " bullets, "1. " lists, **bold**).

export interface FaqItem {
  id: string;
  q: string;
  a: string;
  tags: string[];
}

export const FAQ: FaqItem[] = [
  // --- Toilet, sleep and settling in -----------------------------------------
  {
    id: 'toilet-no-garden',
    q: 'How do we toilet train him without a garden?',
    a: `Pick **one quiet spot** close to the door and go there every time. Carry him down so there are no accidents on the stair.

Go out:

- straight after he wakes
- within about **15 minutes of eating**
- after play
- roughly **every hour** while he’s awake at 8–12 weeks

Reward him the moment he finishes, with a treat and calm praise. Clean accidents indoors with an **enzyme cleaner** and never scold him. Most cockers are reliable by about 4–6 months. Keep a grab bag by the door, with lead, bags, treats and keys, so you can be out in seconds.`,
    tags: ['toilet', 'house training', 'flat', 'garden', 'accidents'],
  },
  {
    id: 'puppy-pads',
    q: 'Should we use puppy pads?',
    a: `A pad by the door is a useful **backup** for overnight and emergencies, especially in a flat with stairs. It shouldn’t be the plan, though.

Pads can teach a puppy that going indoors is fine, which makes the switch to outdoors slower. Keep one pad near the exit, never in his bed area, and still take him out on schedule. Reward outdoor toileting much more generously than pad use. Once he’s reliably going outside and holding on overnight, usually by about 4 months, remove the pad.`,
    tags: ['pads', 'toilet', 'house training', 'flat'],
  },
  {
    id: 'night-crying',
    q: 'He cries at night. What should we do?',
    a: `This is normal for the first nights. He’s just left his mum and littermates.

- **Sleep near him** for the first week or two: his crate by your bed, or one of you on the sofa beside him. Then move it gradually.
- If he cries, **quietly reassure** him, or take him out for a boring toilet trip if it’s been a while.
- **Don’t leave him to “cry it out”.** It doesn’t teach independence, and the RSPCA advises against it.
- Give him a **warm, snug bed**, something that smells of his litter, and a chew.

Most puppies settle within one to two weeks.`,
    tags: ['night', 'crying', 'sleep', 'crate', 'first night'],
  },
  {
    id: 'night-toilet',
    q: 'How often will he need to go out at night?',
    a: `At 8–12 weeks, expect **one or two night trips**. Set an alarm for about 4 hours after the last trip rather than waiting for crying. Keep it boring:

- lights low
- no play
- straight out, then straight back to bed

Most cockers can last the night (around 7–8 hours) by about **4–5 months**. Drop the alarm gradually by pushing it 30 minutes later every few nights. If he suddenly starts needing to go more often, or strains or wees small amounts, see the vet about a possible urine infection.`,
    tags: ['night', 'toilet', 'sleep', 'alarm'],
  },
  {
    id: 'crate',
    q: 'Is a crate cruel?',
    a: `Not when it’s used well. A crate becomes a safe den, keeps him out of trouble when you can’t watch him, and helps with toilet training. Generation Pup found puppies who slept in a crate or closed room overnight had lower odds of separation problems later.

The rules:

- big enough to stand, turn and lie down in
- water always available
- introduced gradually with treats and chews
- **never** used as punishment
- not used for hours on end in the daytime

Leave the door open when he doesn’t need to be shut in. Many cockers choose it for naps.`,
    tags: ['crate', 'sleep', 'den', 'training'],
  },
  {
    id: 'sleep-amount',
    q: 'He sleeps all day. Is that normal?',
    a: `Yes. Young puppies need about **18–20 hours of sleep** a day. A good rhythm is about **45–60 minutes awake**, then a nap.

Over-tired puppies don’t flop. They get bitey, frantic and silly, often in the evening. If Arlo is nipping hard and can’t settle, the answer is usually a **quiet nap in his crate or pen**, not more play.

Protect his sleep from visitors and noise. Adult cockers still sleep about 12–14 hours a day. See the vet if he seems lethargic even when awake, or goes off his food.`,
    tags: ['sleep', 'naps', 'tired', 'zoomies', 'biting'],
  },

  // --- Behaviour and training -------------------------------------------------
  {
    id: 'biting',
    q: 'He bites our hands constantly. How do we stop it?',
    a: `Mouthing is normal puppy behaviour, and cockers are carriers by nature. Teach him what to bite, rather than punishing biting:

- **Keep a toy in your hand** and offer it whenever he mouths you.
- If he bites skin, **stop the game** calmly for a few seconds, then offer the toy.
- **Check the clock.** Bitey spells often mean he’s over-tired and needs a nap.
- Give plenty of **chews**, especially while he’s teething.

Avoid tapping his nose, holding his mouth or shouting, which can make it worse. Most mouthing fades by 6–7 months, when his adult teeth are in.`,
    tags: ['biting', 'mouthing', 'nipping', 'teething'],
  },
  {
    id: 'jumping-up',
    q: 'How do we stop him jumping up at people?',
    a: `Jumping up is a greeting. He wants to reach faces.

- **Reward four paws on the floor.** Drop treats at your feet as you greet him, before he jumps.
- If he jumps, **turn away calmly**, then greet him as soon as his feet are down.
- **Ask for a sit** at the door, and practise it with each other before visitors arrive.
- Give him a **toy to carry** when people arrive. Cockers love to hold something, and a full mouth jumps less.

Everyone, including visitors, needs to follow the same rule.`,
    tags: ['jumping', 'greeting', 'visitors', 'manners'],
  },
  {
    id: 'chewing',
    q: 'What can he safely chew?',
    a: `Chewing is a need, not naughtiness, especially while he’s teething (about 3½–7 months).

**Good options:**

- rubber toys you can stuff with food and freeze
- puppy-safe chew toys
- natural chews suited to his size, such as dried fish skins or a buffalo horn (supervised)
- a damp, frozen flannel for sore gums

**Avoid:** cooked bones, antlers and very hard chews (these can break teeth), rawhide (a choking risk), and anything small enough to swallow whole.

Always supervise chews, and count them in his daily food. Puppy-proof cables, shoes and skirting boards, and swap for a chew when he picks the wrong thing.`,
    tags: ['chewing', 'teething', 'chews', 'toys'],
  },
  {
    id: 'resource-guarding',
    q: 'He growled when we went near his chew. Should we worry?',
    a: `Don’t panic, and **never punish the growl**. It’s a warning, and punishing it teaches him to skip straight to a snap.

For now:

- give chews in his crate or behind a gate, where no one disturbs him
- **walk past and drop a treat**, then keep going
- practise **swap games** away from the chew, and give items back often

Solid-colour male cockers are more prone to guarding, so act early. If it happens more than once, or he freezes, stares or snaps, ask your vet to check for pain and refer you to a qualified behaviourist. See “Behaviour quirks”.`,
    tags: ['resource guarding', 'growling', 'aggression', 'chews', 'food'],
  },
  {
    id: 'recall',
    q: 'When can he go off lead, and how do we teach recall?',
    a: `Start now, indoors. Say his recall word once, then reward generously with a party of treats when he arrives. Play **“ping-pong” recall** between the two of you across the flat.

Outside:

- use a **5–10 m long line** on a harness until his recall is reliable
- ideally, try off-lead in a **secure dog field** first
- always make coming back the best thing that happens, and **never call him to something unpleasant**

Adolescence (about 6–18 months) often brings a recall wobble. Birds are a cocker’s big test, so keep the long line handy through that stage.`,
    tags: ['recall', 'off lead', 'long line', 'training'],
  },
  {
    id: 'pulling',
    q: 'He pulls on the lead. What works?',
    a: `Cockers pull because the smells ahead are exciting, and because pulling works.

- **Use a harness** with a front clip, rather than pulling on his neck.
- **Stop when the lead goes tight.** Move again when it slackens.
- **Reward him often** for being by your side. Pay generously at first.
- **Use a “go sniff” cue** to give him regular sniffing breaks on a loose lead, so he doesn’t have to fight for them.

Practise in quiet places before busy streets. Expect it to take weeks, not days, and don’t use choke or prong collars. Most of all, be consistent: if pulling sometimes works, he’ll keep trying.`,
    tags: ['pulling', 'lead', 'loose lead', 'harness', 'walking'],
  },
  {
    id: 'humping',
    q: 'He keeps humping things. Is that about dominance?',
    a: `No. In puppies, humping is usually **excitement, over-arousal or play**, and it happens in both sexes, neutered or not. It often appears when he’s over-tired or wound up, for example when visitors arrive.

Calmly **interrupt and redirect**, with a toy, a sniffing game or a short nap. Don’t make a big fuss, since attention can reward it.

In adolescent males, some humping is hormonal. If it becomes frequent or obsessive, or he licks his genitals a lot, see the vet.`,
    tags: ['humping', 'mounting', 'behaviour', 'adolescence'],
  },
  {
    id: 'zoomies',
    q: 'He suddenly races round the flat like a mad thing. Is that okay?',
    a: `Those are **zoomies** (frenetic random activity periods). They’re normal, especially in the evening, after a bath or after a poo.

Make sure he can’t crash into anything or skid on slippery floors, then let it burn out. Don’t chase him, which winds him up further.

If zoomies come with frantic biting, he’s usually **over-tired**. Guide him to his crate or pen with a chew for a nap. Regular naps through the day usually reduce the evening madness.`,
    tags: ['zoomies', 'energy', 'evening', 'over-tired'],
  },
  {
    id: 'barking-flat',
    q: 'How do we stop him barking at noises in the flat?',
    a: `Tenement noises (footsteps, doors, the buzzer) are new and exciting to him.

- **Pair noises with treats.** Each time he hears a sound, calmly drop a treat before he barks.
- **Desensitise the buzzer:** play a recording quietly, then treat, and build the volume slowly.
- If he barks, say **“thank you”** calmly, then call him away for a reward.
- **Don’t shout.** To him, it sounds like you’re joining in.
- Make sure he gets enough **exercise and nose work**.

If he barks mainly when left alone, that’s a separation issue. Film him to check.`,
    tags: ['barking', 'noise', 'neighbours', 'flat', 'buzzer'],
  },
  {
    id: 'adolescence',
    q: 'He was so good. Why has he suddenly stopped listening?',
    a: `Welcome to adolescence. It runs from about **6 to 18 months**, and research suggests it peaks around **8 months**. Dogs this age ignore known cues more often, especially with their owners, and may cope less well with being left.

- **Go back a step** in training, and reward generously again.
- **Use the long line** outdoors.
- **Keep sessions short.**
- **Don’t punish.** It damages trust just when he needs it.

It passes. Most cockers are much more settled by 2 years. If he develops new fears or guarding, get help early.`,
    tags: ['adolescence', 'teenager', 'training', 'recall'],
  },
  {
    id: 'alone-time',
    q: 'How long can he be left alone, and does working from home help?',
    a: `Start with **seconds**. Step out of the room, then back, and build very gradually. The RSPCA says adult dogs shouldn’t be left for more than **4 hours**, and puppies much less.

Working from home helps, but it can hide the problem. Arlo may never learn to be alone.

- **Practise daily** even when someone is in: shut a door between you for short spells.
- **Leave him with a chew.**
- **Keep comings and goings boring.**

Cockers are prone to separation anxiety. A camera shows you how he really copes.`,
    tags: ['alone', 'separation', 'working from home', 'left alone'],
  },

  // --- Food ----------------------------------------------------------------------
  {
    id: 'how-much-food',
    q: 'How much should we feed him?',
    a: `Start with the amount on his food’s pack for his **expected adult weight** (about 14–16 kg) and current age. **Weigh it** on kitchen scales, since cups are inaccurate.

- **8–12 weeks:** 4 meals a day
- **3–6 months:** 3 meals
- **from 6 months:** 2 meals

Take training treats out of his daily amount. Then adjust by body condition, not the pack: you should easily feel his ribs and see a waist. Weigh him every 2–4 weeks and log it. If he’s leaving food, has diarrhoea, or is gaining or losing fast, ask the vet.`,
    tags: ['feeding', 'food', 'portions', 'meals', 'diet'],
  },
  {
    id: 'treats',
    q: 'How many treats is too many?',
    a: `Treats should be **no more than 10%** of his daily calories. With a food-driven cocker, that’s easy to blow.

- **Use part of his daily kibble** for training.
- **Make treats pea-sized.**
- **Try low-calorie extras** like green beans or carrot.
- **Count chews.** A big dental chew can be a tenth of his day on its own.

Agree a daily allowance between you, and put it in a pot each morning.

**Never** give grapes, raisins, chocolate, onions, xylitol (sugar-free products, some peanut butters) or cooked bones. If he eats any of these, **call the vet now**.`,
    tags: ['treats', 'food', 'training', 'toxic', 'weight'],
  },
  {
    id: 'weight',
    q: 'How do we know if he’s the right weight?',
    a: `Use your hands, since cocker feathering hides a lot.

- You should **feel his ribs easily** under a thin layer, like the back of your hand.
- From above, he should have a visible **waist** behind his ribs.
- From the side, his **tummy should tuck up**.

About 1 in 10 UK cockers has been diagnosed as obese, and many more are simply overweight. If he’s getting chunky, cut his food by about 10%, cut treats, and recheck in 2–4 weeks. If he gains weight without eating more, or loses weight unexpectedly, see the vet.`,
    tags: ['weight', 'obesity', 'body condition', 'diet'],
  },
  {
    id: 'adult-size',
    q: 'How big will he get?',
    a: `The breed standard for males is **39–41 cm** at the shoulder and **13–14.5 kg**. Real UK pet males average a little heavier, with a median of about **16 kg** (VetCompass). Working-type cockers are often lighter, at around 12–14 kg.

A rough growth guide for a male:

- **12 weeks:** 4–6 kg
- **6 months:** 9–12 kg
- **12 months:** 13–15.5 kg

He’ll reach adult height by about 9–12 months, then fill out until around 18–24 months. Judge him by body condition, not by a target number.`,
    tags: ['size', 'weight', 'growth', 'height', 'adult'],
  },
  {
    id: 'eating-grass',
    q: 'He eats grass. Is he ill?',
    a: `Probably not. Grass-eating is very common in healthy dogs. Many simply seem to like it, and only a minority are sick afterwards.

Things to check:

- Keep him on **monthly lungworm cover**, since slugs and snails live in grass.
- Avoid grass that may have been treated with **weedkiller or fertiliser**.

See the vet if he’s eating grass frantically, is repeatedly sick, is off his food, or his poo changes.`,
    tags: ['grass', 'eating', 'vomiting', 'lungworm'],
  },
  {
    id: 'eating-poo',
    q: 'He eats poo. Why, and how do we stop it?',
    a: `Poo-eating (coprophagia) is common in puppies, and scavenging cockers are keen offenders. It usually fades with age.

- **Pick up** straight away, both his own and any you see on walks.
- **Teach “leave it”**, and reward him well for walking past.
- **Keep him on a lead** in areas where other dogs have fouled.
- **Don’t punish him.** It can make him eat it faster to hide the evidence.

Check he’s getting the right amount of food, and keep his worming up to date. If an adult dog suddenly starts, or he seems hungry all the time, ask the vet.`,
    tags: ['poo', 'coprophagia', 'scavenging', 'leave it'],
  },

  // --- Out and about -------------------------------------------------------------
  {
    id: 'when-go-out',
    q: 'When can he go out and walk on the ground?',
    a: `When your vet says so. It depends on the vaccine brand, usually **1–3 weeks after his second injection**. Arlo HQ stores the date the vet gives you.

Until then:

- **Carry him out** for socialisation: traffic, people, buses, other dogs at a distance. A sling helps.
- Only let him meet **healthy, fully vaccinated adult dogs** in private spaces.
- Keep him off public grass, since parvovirus survives in the ground for months.
- For toilet trips, use your quiet, low-traffic spot.

The next few weeks are the key socialisation window, so don’t wait until the jabs are done to start.`,
    tags: ['vaccinations', 'going out', 'socialisation', 'parvo', 'walks'],
  },
  {
    id: 'how-far-walk',
    q: 'How far can we walk him?',
    a: `As a puppy, keep **formal lead walks short**. The RKC’s “5 minutes per month of age, up to twice a day” is a sensible ceiling, though PDSA notes there’s no hard evidence for the exact figure. Pottering, sniffing and free play on soft ground are different, and are fine in moderation.

- **Build gradually** through adolescence to about **45–60 minutes a day** by 9–12 months.
- **Adults** need at least an hour a day, and more for working types.
- **Avoid long runs, jogging and repetitive fetch** until he’s fully grown, at about 12–14 months.`,
    tags: ['walks', 'exercise', 'puppy', 'how far', 'five minute rule'],
  },
  {
    id: 'stairs',
    q: 'Can he use the stairs to our flat?',
    a: `Carry him **up and down until at least 12 weeks**. A Norwegian study linked daily stairs before 3 months with higher hip dysplasia risk.

After that:

- let him climb **up** a few flights slowly on a short lead
- keep **carrying him down** for longer, since jumping down steps is hardest on growing joints
- avoid racing up and down

For later life, get him used to a **harness with a handle** now, so you can help him on the stairs as a senior.`,
    tags: ['stairs', 'flat', 'joints', 'hips', 'carry'],
  },
  {
    id: 'other-dogs',
    q: 'How should he meet other dogs?',
    a: `Before his vaccinations are complete, only let him meet **calm, healthy, fully vaccinated adult dogs**, in private gardens or homes. After that:

- **Choose well.** Calm, friendly adults teach better manners than boisterous teenagers.
- **Try puppy classes** with supervised play between suitably matched puppies.
- **Keep on-lead greetings short**, about 3 seconds, then move on. Better still, often just walk past, so he learns not every dog is a playmate.
- **Watch his body language.** If he’s hiding or overwhelmed, step in and give him space.

Quality beats quantity. A few good experiences are worth more than lots of chaotic ones.`,
    tags: ['dogs', 'socialisation', 'play', 'greeting', 'puppy class'],
  },
  {
    id: 'children',
    q: 'Is a cocker good with children?',
    a: `Generally yes. Cockers are affectionate and playful. As with any puppy, though, it depends on supervision and good habits on both sides.

- Always **supervise** children and puppies together.
- Teach children to **stroke his chest or side**, not hug or kiss him, lean over him or pick him up.
- **Leave him alone** when he’s eating, chewing or sleeping. This matters extra for a solid-colour cocker, who may be prone to guarding.
- Give him somewhere to **retreat** that children don’t follow him into.

Short, calm, positive meetings with visiting children are great socialisation.`,
    tags: ['children', 'kids', 'family', 'safety', 'visitors'],
  },
  {
    id: 'cats',
    q: 'Will he get on with cats?',
    a: `Most cockers can live happily with cats, especially if they meet them as puppies. They are a hunting breed, though, so introductions need care.

- **Set up a safe space for the cat**, with high perches and a dog-free room, using baby gates.
- **Start with Arlo on a lead** or behind a gate, and reward him for looking at the cat and then back at you.
- **Keep sessions short and calm.** Never let him chase, even in play.

If you meet a cat on walks, keep him on a lead. Chasing becomes a habit quickly.`,
    tags: ['cats', 'introductions', 'chasing', 'pets'],
  },
  {
    id: 'rain',
    q: 'Does he need to go out when it’s pouring?',
    a: `For toilet trips, yes. Cockers were bred to work in wet cover and usually cope fine.

Keep walks shorter in very bad weather, and make up for it with **indoor nose games**. After every wet outing:

- dry his **ears** (inside the flap), armpits, belly and **between his toes**
- wet feathering mats quickly

Keep towels and an absorbent mat by the door. A drying coat helps. Puppies, seniors and clipped cockers may appreciate a waterproof coat on cold, wet days.`,
    tags: ['rain', 'weather', 'drying', 'ears', 'Glasgow'],
  },
  {
    id: 'travel',
    q: 'How should he travel in the car or on the train?',
    a: `**Car:** the Highway Code (rule 57) says dogs must be suitably restrained, with a crate, crash-tested harness or dog guard. Start with short, happy trips. Many puppies get travel sick, so avoid feeding just before, and see the vet if it persists.

**Train:** ScotRail lets up to **two dogs travel free** per passenger. Keep him on a lead and on the floor, not on seats. It’s a great way to reach the countryside.

**Glasgow Subway:** pet dogs aren’t allowed, except small animals in a secure carrier. Check SPT’s rules before trying it with a small puppy.`,
    tags: ['travel', 'car', 'train', 'ScotRail', 'Subway', 'car sickness'],
  },
  {
    id: 'dog-friendly-glasgow',
    q: 'Where can we take him around Glasgow?',
    a: `**Parks:**

- **Glasgow Green**, the nearest, and good for long-line work
- **Kelvingrove Park** and **Queen’s Park**
- **Pollok Country Park**, the city’s largest park, with woodland to sniff
- **Secure dog fields** for hire around Glasgow give safe off-lead time

**Further afield:** Mugdock Country Park, the Campsies and Loch Lomond, by train or car. Follow the Scottish Outdoor Access Code, which means a lead around livestock and close control from **April to July** where birds nest on the ground.

Many Merchant City cafés and pubs welcome dogs. Check before you go.`,
    tags: ['Glasgow', 'parks', 'dog friendly', 'walks', 'pubs', 'cafes'],
  },
  {
    id: 'fireworks',
    q: 'Fireworks season starts just after he arrives. How do we help him?',
    a: `- **Start sound therapy now:** Dogs Trust’s free tracks, played very quietly during meals and play, then louder over days.
- **Walk before dusk** from late October.
- **Close curtains** and put on the TV or music on firework nights.
- **Build a den**, such as his crate covered with a blanket, and let him hide.
- **Stay with him, and comfort him if he wants it.** You can’t “reward” fear, and a calm presence helps.
- **Keep windows and doors shut**, and make sure his tag is on.

If he’s very frightened, speak to the vet. Medication can help, and it needs arranging in advance.`,
    tags: ['fireworks', 'noise', 'fear', 'bonfire night', 'sound therapy'],
  },
  {
    id: 'holidays',
    q: 'What do we do with him when we go away?',
    a: `**Options:**

- **Friends or family he knows**, often the least stressful
- a **home boarder** or **pet sitter**
- a **boarding kennel**

Check that boarders and kennels are **licensed by the council** and insured, and do a short trial stay first.

Nearly all require the **kennel cough vaccine**, given at least **3 weeks** before, plus up-to-date boosters. Pack his usual food, bed and a worn T-shirt, and leave your vet and insurance details.

Book early for Christmas and summer. As a velcro breed, cockers often prefer a home setting to kennels.`,
    tags: ['holidays', 'kennels', 'boarding', 'sitter', 'kennel cough'],
  },
  {
    id: 'puppy-class',
    q: 'When should he start puppy classes?',
    a: `**Book now.** Good classes fill weeks ahead. Many accept puppies after the first vaccination if the venue is clean and indoors. Check with the trainer and your vet.

**Look for:**

- reward-based methods, with no corrections, choke chains or “alpha” talk
- small groups
- a trainer with recognised qualifications (for example ABTC, APDT UK or IMDT)
- calm settling practice as well as play

A good class covers recall, loose-lead walking, settling, handling and polite greetings, and gives you both a support network.`,
    tags: ['puppy class', 'training', 'trainer', 'socialisation'],
  },

  // --- Grooming ------------------------------------------------------------------
  {
    id: 'grooming-frequency',
    q: 'How often does he need grooming?',
    a: `- **Every day:** 2–5 minutes of brushing and handling now, and about 5 minutes once his feathering grows in.
- **Weekly:** a thorough comb to the skin, and an ear check.
- **Every 2–4 weeks:** nails, plus a trim of his foot hair.
- **Groomer:** a short, gentle introduction at about **12–16 weeks**, once vaccinated. Then a full groom **every 6–8 weeks** once his adult coat is in.

Some owners manage with a groomer every 3 months by doing more at home. Ask about hand-stripping versus clipping, since clipping can make a cocker’s coat woollier over time.`,
    tags: ['grooming', 'brushing', 'groomer', 'coat'],
  },
  {
    id: 'ears',
    q: 'How do we look after his ears?',
    a: `About 1 in 10 cockers gets an ear infection each year, so ears matter.

- **Check once or twice a week.** Healthy ears are pale pink, clean and don’t smell.
- **Dry them** after rain, baths and swimming.
- **Keep the hair trimmed** under the flap.
- **Clean only if the vet advises it**, and never push cotton buds into the canal.
- **Don’t pluck** inside the ear canal routinely.

See the vet within a day or two if he shakes his head, scratches, has a smell, discharge or redness. A sudden, painful ear after a walk could be a grass seed, so see the vet the same day.`,
    tags: ['ears', 'ear infection', 'otitis', 'cleaning', 'grass seeds'],
  },
  {
    id: 'bathing',
    q: 'How often can we bath him?',
    a: `Only when he needs it: when he’s rolled in something, or is muddy and smelly. For many cockers that’s every month or two. Frequent bathing strips natural oils. Rinsing off mud with plain water is fine as often as needed.

- **Use a dog shampoo**, not a human one.
- **Brush out knots before bathing**, since water tightens mats.
- **Rinse thoroughly.**
- **Dry him fully,** especially his ears, armpits and feathering.
- Put **cotton wool loosely at the ear entrance** to keep water out, and remove it afterwards.

Make early baths short and fun, with a lick mat stuck to the tiles.`,
    tags: ['bathing', 'shampoo', 'washing', 'grooming'],
  },
  {
    id: 'nails',
    q: 'How do we cut his nails?',
    a: `Trim every **2–4 weeks**, just the tips. If you hear clicking on hard floors, they’re too long.

1. Start by just **touching his paws** and giving treats, for days.
2. **Clip one nail** at a time, taking tiny slivers, then treat. One or two nails a session is fine.
3. **Don’t forget the dew claws** on the inside of his front legs.

Each nail has a blood vessel (the quick), which is hard to see in dark nails. If you nick it, press on some styptic powder. A vet nurse or groomer can show you how, and a grinder is an alternative.`,
    tags: ['nails', 'claws', 'grooming', 'paws'],
  },
  {
    id: 'teeth',
    q: 'Do we really need to brush his teeth?',
    a: `Yes. Dental disease is the **most common problem in UK cockers**, affecting about 1 in 5. Daily brushing is the best prevention.

1. Let him lick **dog toothpaste** off your finger. Never use human toothpaste.
2. Rub your finger along his gums.
3. Move on to a finger brush, then a soft dog toothbrush.
4. Brush the outer surfaces, about 30 seconds a side.

Chews help a little but don’t replace brushing. His adult teeth are all in by 6–7 months. Ask the vet to check for retained puppy teeth then.`,
    tags: ['teeth', 'dental', 'brushing', 'toothpaste'],
  },

  // --- Health --------------------------------------------------------------------
  {
    id: 'vaccines',
    q: 'Which vaccinations does he need?',
    a: `**Primary course:** two injections, usually at about **8 and 10–12 weeks**. They cover distemper, hepatitis, parvovirus and leptospirosis. The breeder’s vet may already have given the first.

**Boosters:** leptospirosis every year, and distemper, hepatitis and parvovirus every 3 years, so there’s a vet visit every year.

**Kennel cough:** an optional nose-drop vaccine. Most kennels, daycares, walkers and classes require it.

Some guidelines suggest an extra puppy dose at 16 weeks or later, and a check at about 6 months. Your vet will advise on their schedule.`,
    tags: ['vaccinations', 'jabs', 'boosters', 'kennel cough', 'vet'],
  },
  {
    id: 'worms-fleas',
    q: 'What about worms, fleas, ticks and lungworm?',
    a: `- **Worms:** every 2 weeks until 12 weeks old, then monthly until 6 months, then as your vet advises. Follow the product’s instructions.
- **Lungworm:** now common across much of the UK, and spread by slugs and snails. Scavenging cockers are at risk, so **monthly prescription cover** is sensible. Only vet products cover it.
- **Fleas:** monthly treatment once he’s old and heavy enough for the product.
- **Ticks:** most active from March to October. Check his ears, armpits and feet after countryside walks, and remove any with a tick hook.

Many vet products combine several of these.`,
    tags: ['worms', 'fleas', 'ticks', 'lungworm', 'parasites'],
  },
  {
    id: 'normal-poo',
    q: 'What does normal puppy poo look like, and when is diarrhoea a problem?',
    a: `Normal poo is brown, **firm and log-shaped**, and easy to pick up. Softer stools are common for a few days after coming home or after a food change.

If he’s bright and eating, a single loose poo is usually nothing to worry about. Keep an eye on him and make sure he’s drinking.

**See the vet the same day if:**

- diarrhoea lasts more than **24 hours**
- there’s blood or it looks like black tar
- he’s also vomiting, lethargic or off his food

**Call the vet now** if he has repeated vomiting or bloody diarrhoea before his vaccinations are complete. It could be parvovirus.`,
    tags: ['poo', 'diarrhoea', 'stools', 'parvo', 'tummy'],
  },
  {
    id: 'vomiting',
    q: 'He’s been sick. Should we worry?',
    a: `A single vomit in a bright, playful puppy who then eats normally is often nothing. Watch him closely, keep water available, and note what came up.

**Call the vet the same day** if he vomits more than once, can’t keep water down, or seems quiet or off his food. Young puppies dehydrate quickly.

**Call the vet now if:**

- there’s blood
- he retches without bringing anything up, with a swollen belly
- he might have eaten something toxic or a foreign object
- he’s unvaccinated and has diarrhoea too
- he collapses`,
    tags: ['vomiting', 'sick', 'emergency', 'poisoning'],
  },
  {
    id: 'neutering',
    q: 'Should we neuter him, and when?',
    a: `The BVA and BSAVA (2026) say castrating a male dog should be a **case-by-case decision**, not routine, and **not before he’s physically mature**.

For a cocker, weigh it up carefully:

- **Behaviour:** castration can make **fear-based aggression worse**, so get a behaviour-informed opinion if he shows any guarding or anxiety.
- **Coat:** many neutered cockers grow a woollier coat that needs more grooming.
- **Benefits:** it can reduce roaming, marking and some hormone-related problems.

A temporary hormone implant lets you “trial” the effects first. Discuss it with your vet when he’s about 12–18 months old.`,
    tags: ['neutering', 'castration', 'hormones', 'behaviour'],
  },
  {
    id: 'insurance',
    q: 'Do we need pet insurance, and what type?',
    a: `Yes. **Insure him from day one**. Anything that happens before a policy starts is excluded, and new policies have waiting periods, often 14 days for illness. Breeders often include 4–5 weeks of free cover. Set up your own policy before it ends.

For a cocker, a **lifetime policy** is best. The limit resets every year, and ongoing conditions (ears, eyes, skin) stay covered as long as you renew without a break.

Check:

- the annual vet-fee limit (higher is safer for a breed prone to long-term conditions)
- the excess
- whether behaviour treatment and dental illness are covered

Avoid time-limited and accident-only policies for this breed.`,
    tags: ['insurance', 'lifetime policy', 'vet bills', 'cost'],
  },
  {
    id: 'lifespan',
    q: 'How long do cockers live?',
    a: `UK studies put the median at about **11½–13½ years**:

- **VetCompass vet records:** 11.4 years overall (females 12.0, males 11.0)
- **2024 study of 584,000 UK dogs:** 13.3 years for cockers, above the all-dog median

Many reach 14–15.

The biggest things you can do for a long life:

- keep him **lean**
- **brush his teeth**
- deal with **ear and eye problems** early
- keep up with **annual vet checks**, and twice-yearly checks from about 8 years old`,
    tags: ['lifespan', 'life expectancy', 'age', 'senior'],
  },
  {
    id: 'emergency',
    q: 'What counts as an emergency?',
    a: `**Call the vet now** if he:

- collapses, has difficulty breathing, or has pale, blue or grey gums
- has a seizure lasting more than 3–5 minutes, or several in a row
- has a swollen, tight belly and retches without bringing anything up
- may have eaten something toxic, like chocolate, grapes, xylitol, medicines or rat bait
- has repeated vomiting or bloody diarrhoea, especially before he’s fully vaccinated
- is bleeding heavily or has been hit by a car
- has an eye injury, or a red, painful, cloudy eye
- is straining without passing urine
- shows signs of heatstroke

Emergency numbers are in Contacts.`,
    tags: ['emergency', 'vet', 'urgent', 'poisoning', 'collapse'],
  },
];
